import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Routes } from "../../constants/RouteConstants";
import { useSwipeable } from "react-swipeable";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import { ApiTypes } from "../../constants/Constants";
import moment from "moment";
import Skeleton from "../../components/ui/Skeleton";
import PageLayout from "../../components/ui/PageLayout";
import binIcon from '../../assets/images/menu/bin-icon.svg'
import justivideLogoIcon from "../../assets/images/Justivide-PrimaryIcon.svg"


interface Notification {
  created_at: string
  description: string
  message: string
  id: number
  isRead: boolean
  notificationType: number
  senderId: any
  title: string
  type: number
  updated_at: string
  userId: number
}

interface GroupedNotifications {
  [key: string]: Notification[];
}

const NotificationItem: React.FC<{
  notification: Notification;
  isSwiped: boolean;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onDelete: () => void;
}> = ({ notification, isSwiped, onSwipeLeft, onSwipeRight, onDelete }) => {
  const swipeHandlers = useSwipeable({
    onSwipedLeft: onSwipeLeft,
    onSwipedRight: onSwipeRight,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const time = moment(notification.created_at).fromNow();

  return (
    <div
      className={`notification-item ${isSwiped ? "swiped" : ""}`}
      {...swipeHandlers}
    >
      <div className="notification-content">
        <div className="title-time">
          <div className="notification-title">{notification.title}</div>
          <span className="time">{time}</span>
        </div>
        <div className="message">{notification.message}</div>
      </div>
      <div
        className="delete-button"
        style={{ display: isSwiped ? "flex" : "none" }}
        onClick={onDelete}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="bin-icon"
        >
          <path
            d="M7.49902 9.58594H12.499"
            stroke="currentColor"
            strokeWidth="1.04167"
            strokeLinejoin="round"
          />
          <path
            d="M8.74902 12.9141H11.249"
            stroke="currentColor"
            strokeWidth="1.04167"
            strokeLinejoin="round"
          />
          <path
            d="M16.249 4.58594L15.4157 18.3359H4.58236L3.74902 4.58594"
            stroke="currentColor"
            strokeWidth="1.04167"
            strokeLinejoin="round"
          />
          <path
            d="M1.66797 4.58073H6.66797M6.66797 4.58073L7.91797 1.66406H12.0846L13.3346 4.58073M6.66797 4.58073H13.3346M18.3346 4.58073H13.3346"
            stroke="currentColor"
            strokeWidth="1.04167"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};

function Notifications() {

  const notification = useMakeRequest();
  const removeNotification = useMakeRequest();

  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [swipedId, setSwipedId] = useState<number | null>(null);

  useEffect(() => {
    getNotificationList();
  }, [])

  const getNotificationList = async () => {
    notification.makeRequest({ url: ApiConstants?.Notification, type: ApiTypes?.POST })
  }

  useEffect(() => {
    if (notification?.status) {
      console.log("notification?.status", notification)
      setNotifications(notification.data?.data?.rows)
    }
  }, [notification.status])

  useEffect(() => {
    if (removeNotification?.status) {
      console.log("removeNotification?.status", removeNotification)
      getNotificationList();
    }
  }, [removeNotification.status])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const swipedEl = document.querySelector(".notification-item.swiped");
      if (swipedEl && !swipedEl.contains(e.target as Node)) {
        setSwipedId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = (id: number) => {
    removeNotification.makeRequest({
      url: ApiConstants?.NotificationRemove, payload: {
        isAllDelete: false,
        notificationId: id
      }
    })
  };

  const onPressClearAll = () => {
    removeNotification.makeRequest({
      url: ApiConstants?.NotificationRemove, payload: {
        isAllDelete: true
      }
    })
  }

  // Group notifications by date
  const groupNotificationsByDate = (notifications: Notification[]): GroupedNotifications => {
    const grouped: GroupedNotifications = {};

    notifications.forEach((notification) => {
      const notificationDate = moment(notification.created_at);
      const today = moment().startOf('day');
      const yesterday = moment().subtract(1, 'day').startOf('day');

      let dateKey: string;

      if (notificationDate.isSame(today, 'day')) {
        dateKey = 'TODAY';
      } else if (notificationDate.isSame(yesterday, 'day')) {
        dateKey = 'YESTERDAY';
      } else {
        dateKey = notificationDate.format('MM/DD/YYYY');
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }

      grouped[dateKey].push(notification);
    });

    return grouped;
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  // Sort the date keys to maintain order: TODAY, YESTERDAY, then chronological
  const sortedDateKeys = Object.keys(groupedNotifications).sort((a, b) => {
    if (a === 'TODAY') return -1;
    if (b === 'TODAY') return 1;
    if (a === 'YESTERDAY') return -1;
    if (b === 'YESTERDAY') return 1;

    return moment(b, 'MM/DD/YYYY').diff(moment(a, 'MM/DD/YYYY'));
  });

  console.log("sortedDateKeys: ", sortedDateKeys)

  return (
    <>
      <PageLayout headerName='Notifications' onlyTitle={false}>
        <div className="notification-page">
          <div className="notification">
            <div>
              <div className="notification-list">
                {notification.loading ? Array.from({ length: 3 })?.map((item, index) => (
                  <li key={index} style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Skeleton
                        className='skeleton-loader'
                        style={{ width: '80px', height: '20px', borderRadius: '4px' }}
                      />
                      <Skeleton
                        className='skeleton-loader'
                        style={{ width: '40px', height: '16px', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Skeleton
                        className='skeleton-loader'
                        style={{ width: '100%', height: '16px', marginBottom: '6px', borderRadius: '4px' }}
                      />
                      <Skeleton
                        className='skeleton-loader'
                        style={{ width: '90%', height: '16px', borderRadius: '4px' }}
                      />
                    </div>
                  </li>
                )) :
                  <>
                    {notifications?.length > 0 ? (
                      sortedDateKeys.map((dateKey) => (
                        <div key={dateKey} className="notification-group">
                          <div className="notification-date-header d-flex justify-content-between align-items-center">
                            <h3>{dateKey}</h3>
                            {dateKey == 'TODAY' &&
                              <label
                                className="clear-notification"
                                onClick={onPressClearAll}
                              >
                                Clear All
                              </label>
                            }
                          </div>
                          {groupedNotifications[dateKey].map((item) => (
                            <NotificationItem
                              key={item.id}
                              notification={item}
                              isSwiped={swipedId === item.id}
                              onSwipeLeft={() => setSwipedId(item.id)}
                              onSwipeRight={() => setSwipedId(null)}
                              onDelete={() => handleDelete(item.id)}
                            />
                          ))}
                        </div>
                      ))
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
                        <img className='w-25 rounded-3 mx-auto d-block' src={justivideLogoIcon} alt="" />
                        <p className="text-center mt-3">No Record Found</p>
                      </div>
                    )}
                  </>
                }
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export default Notifications;